/**
 * SignIn Page - Ultra-Simple Standalone Login
 *
 * Brand new login page to bypass ALL caching issues
 * Uses direct fetch() calls, no tRPC, no complex state management
 */

import React, { useState } from 'react';
import { COLORS, TYPOGRAPHY, SPACING } from '../styles/theme';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔐 SignIn: Starting login...');

      // Direct fetch call - no axios, no tRPC, nothing fancy
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
      const response = await fetch(`${apiBaseUrl}/api/simple-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const data = await response.json();
      console.log('📦 SignIn: Response:', { status: response.status, hasUser: !!data.user, hasToken: !!data.accessToken });

      if (response.ok && data.accessToken && data.user) {
        console.log('✅ SignIn: Success! Saving credentials...');

        // Save to localStorage
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));

        console.log('🚀 SignIn: Redirecting to dashboard...');

        // Force full page reload to dashboard
        window.location.href = '/';
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (err: any) {
      console.error('❌ SignIn: Error:', err);
      setError(err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1f2e 0%, #2d1b3d 100%)',
      padding: SPACING.xl
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1A1F2E, #242938)',
        backdropFilter: 'blur(12px)',
        border: `2px solid ${COLORS.primary.orange}`,
        borderRadius: 0,
        padding: SPACING.xxl,
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 0 30px rgba(255, 107, 53, 0.5), inset 0 0 30px rgba(255, 107, 53, 0.1)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: SPACING.xxl }}>
          <h1 style={{
            fontFamily: TYPOGRAPHY.fontFamily.primary,
            fontSize: TYPOGRAPHY.fontSize.xxxxl,
            fontWeight: TYPOGRAPHY.fontWeight.bold,
            background: 'linear-gradient(90deg, #FF6B35, #F7931E)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: SPACING.sm,
            letterSpacing: TYPOGRAPHY.letterSpacing.widest,
            textTransform: 'uppercase'
          }}>
            BGS CORE
          </h1>
          <p style={{
            color: COLORS.text.secondary,
            fontSize: TYPOGRAPHY.fontSize.sm,
            textTransform: 'uppercase',
            letterSpacing: TYPOGRAPHY.letterSpacing.wider,
            fontFamily: TYPOGRAPHY.fontFamily.body
          }}>
            Sign In
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${COLORS.border.error}`,
            borderRadius: '8px',
            padding: `${SPACING.md} ${SPACING.lg}`,
            marginBottom: SPACING.xl,
            color: COLORS.status.error,
            fontSize: TYPOGRAPHY.fontSize.sm,
          }} role="alert">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{
              display: 'block',
              marginBottom: SPACING.sm,
              color: COLORS.text.secondary,
              fontSize: TYPOGRAPHY.fontSize.sm,
              fontFamily: TYPOGRAPHY.fontFamily.body,
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="your.email@example.com"
              autoComplete="email"
              style={{
                backgroundColor: '#334155',
                border: '2px solid #2d3748',
                borderRadius: '8px',
                color: '#E0E6F0',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                padding: '12px 16px',
                transition: '0.2s',
                width: '100%',
                outline: 'none',
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: SPACING.xl }}>
            <label style={{
              display: 'block',
              marginBottom: SPACING.sm,
              color: COLORS.text.secondary,
              fontSize: TYPOGRAPHY.fontSize.sm,
              fontFamily: TYPOGRAPHY.fontFamily.body,
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="••••••••"
              autoComplete="current-password"
              style={{
                backgroundColor: '#334155',
                border: '2px solid #2d3748',
                borderRadius: '8px',
                color: '#E0E6F0',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                padding: '12px 16px',
                transition: '0.2s',
                width: '100%',
                outline: 'none',
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: loading ? '#666' : COLORS.primary.orange,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: '0.2s',
              fontFamily: TYPOGRAPHY.fontFamily.body,
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
